import { useEffect, useState } from "react";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Trash2, MessageSquare, Save, BarChart3, Plus } from "lucide-react";

export function AdminCommentsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");

  const load = async () => {
    const r = await api.get("/admin/comments", { params: { status_filter: status } });
    setItems(r.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const moderate = async (c, newStatus) => {
    await api.put(`/admin/comments/${c.id}`, { status: newStatus });
    toast.success(newStatus === "approved" ? "Comentario aprobado" : "Comentario rechazado");
    load();
  };

  const remove = async (c) => {
    if (!window.confirm("¿Eliminar comentario?")) return;
    await api.delete(`/admin/comments/${c.id}`);
    load();
  };

  return (
    <div data-testid="admin-comments-tab">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="font-headline uppercase text-2xl flex items-center gap-2"><MessageSquare size={22} /> Comentarios</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {items.length} en estado <strong>{status}</strong>
          </p>
        </div>
        <div className="flex gap-1 border rounded-sm overflow-hidden text-xs uppercase tracking-widest font-bold" style={{ borderColor: "var(--border)" }}>
          {["pending", "approved", "rejected"].map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className="px-3 py-2"
              style={{
                background: status === s ? "var(--accent)" : "transparent",
                color: status === s ? "#fff" : "var(--text-muted)",
              }}
              data-testid={`comments-filter-${s}`}
            >
              {s === "pending" ? "Pendientes" : s === "approved" ? "Aprobados" : "Rechazados"}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="py-12 text-center" style={{ color: "var(--text-muted)" }}>
          No hay comentarios en este estado.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => (
            <li key={c.id} className="rounded-sm border p-4" style={{ borderColor: "var(--border)" }} data-testid={`comment-row-${c.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <p className="font-bold">{c.name} <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>{c.email}</span></p>
                  <p className="overline" style={{ color: "var(--text-muted)" }}>{timeAgo(c.created_at)} · artículo {c.article_id?.slice(0, 8)}</p>
                </div>
                <div className="flex gap-1">
                  {c.status !== "approved" && <button onClick={() => moderate(c, "approved")} className="p-2 text-green-600 hover:bg-green-50 rounded-sm" title="Aprobar" data-testid={`comment-approve-${c.id}`}><Check size={16} /></button>}
                  {c.status !== "rejected" && <button onClick={() => moderate(c, "rejected")} className="p-2 text-orange-600 hover:bg-orange-50 rounded-sm" title="Rechazar"><X size={16} /></button>}
                  <button onClick={() => remove(c)} className="p-2 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={16} /></button>
                </div>
              </div>
              <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{c.body}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AdminSettingsTab() {
  const [channelId, setChannelId] = useState("");
  const [polls, setPolls] = useState([]);
  const [newPoll, setNewPoll] = useState({ question: "", options: ["", ""] });
  const [saving, setSaving] = useState(false);
  const [layout, setLayout] = useState({ sections: [], live_visible: true, breaking_visible: true, live_position: "top" });
  const [push, setPush] = useState({ title: "", body: "", url: "/", image: "", category_slug: "", require_interaction: false, action_label: "Leer nota" });
  const [navItems, setNavItems] = useState([]);
  const [socialLinks, setSocialLinks] = useState({ instagram: "", tiktok: "", youtube: "", twitter: "", facebook: "" });
  const [aiConfig, setAiConfig] = useState({ summary_enabled: true, popu_ia_enabled: true, provider: "emergent", model: "gpt-4o-mini", custom_base_url: "", custom_api_key: "", custom_api_key_masked: "" });
  const [aiUsage, setAiUsage] = useState({ current_month: "", rows: [] });
  const [countdown, setCountdown] = useState({
    enabled: false, label: "MUNDIAL 2026", title: "CONTEO REGRESIVO PARA",
    accent: "LA FIESTA", subtitle: "DEL FÚTBOL", image_url: "", bg_color: "#0a1535",
    text_color: "#ffffff", accent_color: "#1e6bff", link: "/categoria/deportes",
    start_date: "", end_date: "", next_banner: null, show_overlay_text: false,
  });
  const [nextBannerOn, setNextBannerOn] = useState(false);
  const [wc, setWc] = useState({ enabled: true, hero_overline: "", hero_title: "", hero_subtitle: "", sponsor_logo_url: "", sponsor_link: "", sponsor_label: "AUSPICIA", featured_players: [] });
  const [wcPlayerDraft, setWcPlayerDraft] = useState({ name: "", team: "", country: "", country_flag: "", dorsal: "", age: "", position: "", height: "", weight: "", photo_url: "" });
  const [pushing, setPushing] = useState(false);
  const [transit, setTransit] = useState({ enabled: true, notify_no_transitable: true, notify_conflictos: true, notify_limpieza: false, title_template: "🚧 Alerta de transitabilidad" });
  const [transitRefreshing, setTransitRefreshing] = useState(false);

  const saveTransit = async () => {
    try {
      await api.put("/admin/transitability/config", transit);
      toast.success("Notificaciones de transitabilidad guardadas");
    } catch { toast.error("Error guardando"); }
  };

  const refreshTransit = async () => {
    setTransitRefreshing(true);
    try {
      const r = await api.post("/admin/transitability/refresh");
      toast.success(`Sincronizado: ${r.data.events_count} eventos`);
    } catch { toast.error("Error al sincronizar (puede requerir resolver captcha)"); }
    finally { setTransitRefreshing(false); }
  };

  const load = async () => {
    try {
      const [s, p, l, ai, usage, wcr, tcfg] = await Promise.all([
        api.get("/settings/public"),
        api.get("/admin/polls"),
        api.get("/home/layout"),
        api.get("/admin/ai-config"),
        api.get("/admin/ai-usage"),
        api.get("/worldcup"),
        api.get("/admin/transitability/config"),
      ]);
      setChannelId(s.data.youtube_channel_id || "");
      setNavItems(s.data.mobile_nav || []);
      setSocialLinks({ instagram: "", tiktok: "", youtube: "", twitter: "", facebook: "", ...(s.data.social_links || {}) });
      setAiConfig({ ...ai.data });
      setAiUsage(usage.data || { current_month: "", rows: [] });
      setCountdown((prev) => ({ ...prev, ...(s.data.countdown_banner || {}) }));
      setNextBannerOn(!!(s.data.countdown_banner?.next_banner));
      setTransit({ ...transit, ...(tcfg.data || {}) });
      setWc((prev) => ({
        ...prev,
        enabled: wcr.data?.enabled ?? true,
        hero_overline: wcr.data?.hero_overline || "",
        hero_title: wcr.data?.hero_title || "",
        hero_subtitle: wcr.data?.hero_subtitle || "",
        sponsor_logo_url: wcr.data?.sponsor_logo_url || "",
        sponsor_link: wcr.data?.sponsor_link || "",
        sponsor_label: wcr.data?.sponsor_label || "AUSPICIA",
        featured_players: wcr.data?.featured_players || [],
      }));
      setPolls(p.data || []);
      setLayout(l.data);
    } catch (err) { console.warn("[popu]", err); }
  };

  const saveWc = async (override = {}) => {
    try {
      await api.put("/admin/worldcup", { ...wc, ...override });
      toast.success("Mundial actualizado");
      load();
    } catch { toast.error("Error guardando Mundial"); }
  };

  const addWcPlayer = () => {
    if (!wcPlayerDraft.name) { toast.error("Nombre requerido"); return; }
    const arr = [...(wc.featured_players || []), { ...wcPlayerDraft }];
    setWc({ ...wc, featured_players: arr });
    setWcPlayerDraft({ name: "", team: "", country: "", country_flag: "", dorsal: "", age: "", position: "", height: "", weight: "", photo_url: "" });
    saveWc({ featured_players: arr });
  };

  const removeWcPlayer = (idx) => {
    const arr = (wc.featured_players || []).filter((_, i) => i !== idx);
    setWc({ ...wc, featured_players: arr });
    saveWc({ featured_players: arr });
  };

  const saveAiConfig = async (override = {}) => {
    try {
      const payload = { ...aiConfig, ...override };
      // Avoid sending the masked field
      delete payload.custom_api_key_masked;
      await api.put("/admin/ai-config", payload);
      toast.success("Config IA guardada");
      load();
    } catch (err) {
      toast.error("Error guardando");
    }
  };

  const resetAiUsage = async () => {
    if (!window.confirm("¿Borrar el historial de uso de IA?")) return;
    try {
      await api.post("/admin/ai-usage/reset");
      toast.success("Historial reseteado");
      load();
    } catch (err) {
      toast.error("Error");
    }
  };

  const saveCountdown = async () => {
    try {
      const payload = { ...countdown };
      if (!nextBannerOn) {
        payload.next_banner = null;
      } else {
        payload.next_banner = {
          enabled: true,
          ...(countdown.next_banner || {}),
        };
      }
      await api.put("/admin/countdown-banner", payload);
      toast.success("Banner contador guardado");
    } catch (err) {
      toast.error("Error guardando banner");
    }
  };

  const updateNext = (patch) => {
    setCountdown((c) => ({ ...c, next_banner: { ...(c.next_banner || {}), ...patch } }));
  };

  useEffect(() => { load(); }, []);

  const saveChannel = async () => {
    setSaving(true);
    try {
      await api.post("/admin/youtube/channel", { channel_id: channelId });
      toast.success("Canal de YouTube actualizado");
    } catch (err) {
      toast.error("Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const saveLayout = async () => {
    try {
      await api.put("/admin/home/layout", layout);
      toast.success("Layout guardado");
    } catch (err) {
      toast.error("Error guardando layout");
    }
  };

  const saveSocialLinks = async () => {
    try {
      await api.put("/admin/social-links", socialLinks);
      toast.success("Redes sociales guardadas");
    } catch (err) {
      toast.error("Error guardando redes");
    }
  };

  const moveSection = (idx, dir) => {
    const arr = [...layout.sections];
    const n = idx + dir;
    if (n < 0 || n >= arr.length) return;
    [arr[idx], arr[n]] = [arr[n], arr[idx]];
    setLayout({ ...layout, sections: arr });
  };

  const broadcastPush = async (e) => {
    e.preventDefault();
    setPushing(true);
    try {
      const payload = {
        title: push.title,
        body: push.body,
        url: push.url || "/",
        image: push.image || null,
        category_slug: push.category_slug || null,
        require_interaction: !!push.require_interaction,
        actions: push.action_label ? [{ action: "open", title: push.action_label }] : [],
      };
      const r = await api.post("/admin/push/broadcast", payload);
      toast.success(`Notificación enviada a ${r.data.sent}/${r.data.total} suscriptores`);
      setPush({ title: "", body: "", url: "/", image: "", category_slug: "", require_interaction: false, action_label: "Leer nota" });
    } catch (err) {
      toast.error("Error enviando push");
    } finally {
      setPushing(false);
    }
  };

  const createPoll = async (e) => {
    e.preventDefault();
    const opts = newPoll.options.filter(o => o.trim()).map(o => o.trim());
    if (!newPoll.question.trim() || opts.length < 2) {
      toast.error("Pregunta y al menos 2 opciones");
      return;
    }
    try {
      await api.post("/admin/polls", { question: newPoll.question, options: opts, active: true });
      toast.success("Encuesta creada");
      setNewPoll({ question: "", options: ["", ""] });
      load();
    } catch (err) {
      toast.error("Error creando encuesta");
    }
  };

  const deletePoll = async (id) => {
    if (!window.confirm("¿Eliminar encuesta?")) return;
    await api.delete(`/admin/polls/${id}`);
    load();
  };

  const SECTION_LABELS = {
    breaking: "⚡ Último Momento (banner rojo)",
    live: "🔴 YouTube En Vivo",
    hero: "📰 Hero principal",
    ia: "✨ POPU IA",
    featured: "⭐ Grilla destacadas",
    shorts_social: "📱 Shorts + Redes",
    countdown: "⏱ Contador (Banner)",
    wc_fixture: "🏆 Fixture del Mundial (compacto)",
    wc_results: "⚽ Resultados del Mundial (full-width)",
    flights: "✈️ Vuelos en alerta (BO)",
    transitabilidad: "🚧 Transitabilidad (ABC)",
    opinion: "✍️ Opinión (Columnistas)",
    polls: "🗳️ Encuestas (votación)",
    polls_results: "📊 Resultados de encuestas",
    latest_news: "📰 Últimas noticias",
    recommended_news: "⭐ Recomendados + Newsletter",
    newsletter: "📧 Newsletter (suelto)",
    latest_recommended: "📋 Últimas + Recomendados (combinado, legacy)",
  };

  const ALL_SECTIONS = Object.keys(SECTION_LABELS);
  const missingSections = ALL_SECTIONS.filter((k) => !(layout.sections || []).includes(k));
  const addSection = (k) => setLayout({ ...layout, sections: [...(layout.sections || []), k] });
  const removeSection = (k) => setLayout({ ...layout, sections: (layout.sections || []).filter((x) => x !== k) });

  return (
    <div data-testid="admin-settings-tab" className="space-y-10">
      <section>
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2"><BarChart3 size={20} /> Layout del Home</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Reordená las secciones del home. Usá las flechas para mover arriba/abajo. Ocultá YouTube Live o el banner de Último Momento.
        </p>
        <div className="space-y-2 mb-4">
          {(layout.sections || []).map((k, idx) => (
            <div key={k} className="flex items-center gap-3 p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <span className="font-bold text-sm w-6" style={{ color: "var(--text-muted)" }}>{idx + 1}</span>
              <span className="flex-1 text-sm font-semibold">{SECTION_LABELS[k] || k}</span>
              <div className="flex gap-1">
                <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="px-2 py-1 hover:accent-text disabled:opacity-30">↑</button>
                <button onClick={() => moveSection(idx, 1)} disabled={idx === layout.sections.length - 1} className="px-2 py-1 hover:accent-text disabled:opacity-30">↓</button>
                <button onClick={() => removeSection(k)} className="px-2 py-1 hover:text-red-500" title="Quitar del layout">✕</button>
              </div>
            </div>
          ))}
        </div>
        {missingSections.length > 0 && (
          <div className="mb-4 p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
            <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: "var(--text-muted)" }}>Agregar sección al home</p>
            <div className="flex flex-wrap gap-2">
              {missingSections.map((k) => (
                <button
                  key={k}
                  onClick={() => addSection(k)}
                  data-testid={`add-section-${k}`}
                  className="text-xs px-3 py-1.5 border rounded-full hover:accent-border hover:accent-text transition"
                  style={{ borderColor: "var(--border)" }}
                >
                  + {SECTION_LABELS[k] || k}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-4 mb-3 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={layout.live_visible} onChange={(e) => setLayout({ ...layout, live_visible: e.target.checked })} /> YouTube Live visible</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={layout.breaking_visible} onChange={(e) => setLayout({ ...layout, breaking_visible: e.target.checked })} /> Último Momento visible</label>
        </div>
        <button onClick={saveLayout} className="btn-accent" data-testid="layout-save"><Save size={14} /> Guardar layout</button>
      </section>

      <section>
        <h2 className="font-headline uppercase text-2xl mb-3">YouTube Live</h2>
        <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
          Pegá el ID de tu canal de YouTube (no la URL). Ej: <code>UCxxxxxxxxxxxxxxx</code>.
        </p>
        <div className="flex gap-2">
          <input
            value={channelId}
            onChange={(e) => setChannelId(e.target.value.trim())}
            placeholder="UC..."
            className="flex-1 px-3 py-2 border rounded-sm bg-transparent"
            style={{ borderColor: "var(--border)" }}
            data-testid="yt-channel-input"
          />
          <button onClick={saveChannel} disabled={saving} className="btn-accent disabled:opacity-50" data-testid="yt-channel-save">
            <Save size={14} /> Guardar
          </button>
        </div>
      </section>

      <section>
        <h2 className="font-headline uppercase text-2xl mb-3">Redes sociales (Footer)</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          URLs completas de tus redes (ej: <code>https://instagram.com/popu.news</code>). Las vacías se ocultan del footer.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          {[
            { k: "instagram", label: "Instagram", ph: "https://instagram.com/popu.news" },
            { k: "tiktok", label: "TikTok", ph: "https://tiktok.com/@popu.news" },
            { k: "youtube", label: "YouTube", ph: "https://youtube.com/@popunews" },
            { k: "twitter", label: "X (Twitter)", ph: "https://x.com/popunews" },
            { k: "facebook", label: "Facebook", ph: "https://facebook.com/popunews" },
          ].map(({ k, label, ph }) => (
            <div key={k}>
              <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>
                {label}
              </label>
              <input
                type="url"
                value={socialLinks[k] || ""}
                onChange={(e) => setSocialLinks({ ...socialLinks, [k]: e.target.value })}
                placeholder={ph}
                data-testid={`social-input-${k}`}
                className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
              />
            </div>
          ))}
        </div>
        <button onClick={saveSocialLinks} className="btn-accent" data-testid="social-links-save">
          <Save size={14} /> Guardar redes
        </button>
      </section>

      <section data-testid="transitability-section">
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2">🚧 Transitabilidad — Notificaciones</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Datos en vivo desde la <strong>Administradora Boliviana de Carreteras (ABC)</strong>. Configurá qué alertas disparan notificaciones push automáticas. El sistema chequea cada 10 minutos y solo notifica eventos nuevos.
        </p>
        <div className="space-y-3 mb-4">
          <label className="flex items-center justify-between p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="pr-3">
              <p className="font-bold text-sm">Módulo activo</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Si está apagado, no se muestra el módulo en la home ni se envían notificaciones.</p>
            </div>
            <input type="checkbox" checked={transit.enabled} onChange={(e) => setTransit({ ...transit, enabled: e.target.checked })} data-testid="transit-enabled" />
          </label>
          <label className="flex items-center justify-between p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="pr-3">
              <p className="font-bold text-sm">🔴 No transitable / Tráfico cerrado</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Notificar cuando aparezca un tramo nuevo con estado NO TRANSITABLE.</p>
            </div>
            <input type="checkbox" checked={transit.notify_no_transitable} onChange={(e) => setTransit({ ...transit, notify_no_transitable: e.target.checked })} data-testid="transit-notify-nt" />
          </label>
          <label className="flex items-center justify-between p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="pr-3">
              <p className="font-bold text-sm">🟠 Conflictos sociales</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Notificar cuando aparezca un tramo cortado por conflicto/bloqueo.</p>
            </div>
            <input type="checkbox" checked={transit.notify_conflictos} onChange={(e) => setTransit({ ...transit, notify_conflictos: e.target.checked })} data-testid="transit-notify-cs" />
          </label>
          <label className="flex items-center justify-between p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="pr-3">
              <p className="font-bold text-sm">🟡 Obstrucción por trabajos de limpieza</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Notificar también obstrucciones por limpieza/derrumbe (suele ser menos urgente).</p>
            </div>
            <input type="checkbox" checked={transit.notify_limpieza} onChange={(e) => setTransit({ ...transit, notify_limpieza: e.target.checked })} data-testid="transit-notify-li" />
          </label>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>
              Título de la notificación
            </label>
            <input
              type="text"
              value={transit.title_template}
              onChange={(e) => setTransit({ ...transit, title_template: e.target.value })}
              placeholder="🚧 Alerta de transitabilidad"
              className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm"
              style={{ borderColor: "var(--border)" }}
              data-testid="transit-title-input"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={saveTransit} className="btn-accent" data-testid="transit-save">
            <Save size={14} /> Guardar config
          </button>
          <button onClick={refreshTransit} disabled={transitRefreshing} className="btn-accent disabled:opacity-50" style={{ background: "#2563eb" }} data-testid="transit-refresh">
            {transitRefreshing ? "Sincronizando..." : "Sincronizar ahora"}
          </button>
        </div>
      </section>

      <section data-testid="countdown-banner-section">
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2">⏱ Banner Contador</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Banner con cuenta regresiva. Aparece en el home según la posición que elijas en "Layout del Home" (sección "Contador"). Se muestra entre las fechas de inicio y fin.
        </p>
        <label className="flex items-center justify-between p-3 border rounded-sm mb-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="pr-3">
            <p className="font-bold text-sm">Activar banner contador</p>
            <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Apagado: no aparece aunque esté en el layout.</p>
          </div>
          <input type="checkbox" checked={!!countdown.enabled} onChange={(e) => setCountdown({ ...countdown, enabled: e.target.checked })} className="w-5 h-5" data-testid="countdown-enabled" style={{ accentColor: "var(--accent)" }} />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Fecha inicio</label>
            <input type="datetime-local" value={countdown.start_date?.slice(0,16) || ""} onChange={(e) => setCountdown({ ...countdown, start_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="countdown-start" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Fecha fin (objetivo)</label>
            <input type="datetime-local" value={countdown.end_date?.slice(0,16) || ""} onChange={(e) => setCountdown({ ...countdown, end_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="countdown-end" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Texto izquierda (ej. MUNDIAL 2026)</label>
            <input value={countdown.label || ""} onChange={(e) => setCountdown({ ...countdown, label: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Link destino (ej. /categoria/deportes)</label>
            <input value={countdown.link || ""} onChange={(e) => setCountdown({ ...countdown, link: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Título principal</label>
            <input value={countdown.title || ""} onChange={(e) => setCountdown({ ...countdown, title: e.target.value })} placeholder="CONTEO REGRESIVO PARA" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Texto acento</label>
            <input value={countdown.accent || ""} onChange={(e) => setCountdown({ ...countdown, accent: e.target.value })} placeholder="LA FIESTA" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Subtítulo</label>
            <input value={countdown.subtitle || ""} onChange={(e) => setCountdown({ ...countdown, subtitle: e.target.value })} placeholder="DEL FÚTBOL" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Imagen de fondo (URL)</label>
            <input value={countdown.image_url || ""} onChange={(e) => setCountdown({ ...countdown, image_url: e.target.value })} placeholder="https://..." className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            {countdown.image_url && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!countdown.show_overlay_text}
                  onChange={(e) => setCountdown({ ...countdown, show_overlay_text: e.target.checked })}
                  className="w-4 h-4"
                  style={{ accentColor: "var(--accent)" }}
                  data-testid="countdown-show-overlay-text"
                />
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                  Mostrar texto (MUNDIAL 2026, CONTEO REGRESIVO...) <b>sobre</b> la imagen
                </span>
              </label>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Color fondo</label>
            <input type="color" value={countdown.bg_color || "#0a1535"} onChange={(e) => setCountdown({ ...countdown, bg_color: e.target.value })} className="w-full h-10 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Color texto</label>
            <input type="color" value={countdown.text_color || "#ffffff"} onChange={(e) => setCountdown({ ...countdown, text_color: e.target.value })} className="w-full h-10 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Color acento (números + accent)</label>
            <input type="color" value={countdown.accent_color || "#0040FF"} onChange={(e) => setCountdown({ ...countdown, accent_color: e.target.value })} className="w-full h-10 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
          </div>
        </div>
        <button onClick={saveCountdown} className="btn-accent mb-3" data-testid="countdown-save">
          <Save size={14} /> Guardar banner contador
        </button>

        {/* ──────── NEXT BANNER (cuando el contador llega a 0) ──────── */}
        <div className="mt-6 p-4 rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }} data-testid="next-banner-section">
          <label className="flex items-center justify-between mb-3">
            <div className="pr-3">
              <p className="font-bold text-sm uppercase tracking-wider">🔁 Banner siguiente (al llegar a 0)</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>
                Cuando el contador termina, se muestra este banner. Si le pones una fecha fin, también será un nuevo contador.
              </p>
            </div>
            <input
              type="checkbox"
              checked={nextBannerOn}
              onChange={(e) => setNextBannerOn(e.target.checked)}
              className="w-5 h-5"
              data-testid="next-banner-enabled"
              style={{ accentColor: "var(--accent)" }}
            />
          </label>

          {nextBannerOn && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Texto izquierda (label)</label>
                <input value={countdown.next_banner?.label || ""} onChange={(e) => updateNext({ label: e.target.value })} placeholder="¡EMPIEZA!" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-label" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Link destino</label>
                <input value={countdown.next_banner?.link || ""} onChange={(e) => updateNext({ link: e.target.value })} placeholder="/categoria/deportes" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-link" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Título principal</label>
                <input value={countdown.next_banner?.title || ""} onChange={(e) => updateNext({ title: e.target.value })} placeholder="YA COMENZÓ" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-title" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Texto acento</label>
                <input value={countdown.next_banner?.accent || ""} onChange={(e) => updateNext({ accent: e.target.value })} placeholder="LA FIESTA" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-accent" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Subtítulo</label>
                <input value={countdown.next_banner?.subtitle || ""} onChange={(e) => updateNext({ subtitle: e.target.value })} placeholder="DEL FÚTBOL" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-subtitle" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Imagen de fondo (URL, opcional)</label>
                <input value={countdown.next_banner?.image_url || ""} onChange={(e) => updateNext({ image_url: e.target.value })} placeholder="https://..." className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-image" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Nueva fecha fin (opcional → segundo countdown)</label>
                <input type="datetime-local" value={countdown.next_banner?.end_date?.slice(0,16) || ""} onChange={(e) => updateNext({ end_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="next-end" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Color fondo / texto / acento</label>
                <div className="flex gap-2">
                  <input type="color" value={countdown.next_banner?.bg_color || "#0a1535"} onChange={(e) => updateNext({ bg_color: e.target.value })} className="flex-1 h-10 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
                  <input type="color" value={countdown.next_banner?.text_color || "#ffffff"} onChange={(e) => updateNext({ text_color: e.target.value })} className="flex-1 h-10 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
                  <input type="color" value={countdown.next_banner?.accent_color || "#1e6bff"} onChange={(e) => updateNext({ accent_color: e.target.value })} className="flex-1 h-10 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} />
                </div>
              </div>
            </div>
          )}

          <button onClick={saveCountdown} className="btn-accent mt-4" data-testid="next-save">
            <Save size={14} /> Guardar todo
          </button>
        </div>
      </section>

      <section data-testid="airline-logos-section">
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2">✈️ Logos de aerolíneas (widget Vuelos)</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Pegá la URL del logo PNG/SVG para cada aerolínea. Si no hay URL, se usa un badge con color de marca automáticamente. Formato recomendado: PNG cuadrado o vertical con fondo transparente, max 200×200 px.
        </p>
        <AirlineLogosEditor />
      </section>

      <section data-testid="social-posts-section">
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2">📱 Posts de redes (sección DE REDES)</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Cards que aparecen en la sección "DE REDES" del home. Agregá publicaciones reales de Instagram, TikTok, X, Facebook o YouTube. El número de caracteres del texto se respeta tal cual lo pegues.
        </p>
        <SocialPostsEditor />
      </section>

      <section data-testid="worldcup-admin-section">
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2">🏆 Mundial 2026</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Aparece arriba de los artículos en <code>/categoria/deportes</code>. Datos del fixture, grupos, selecciones y posiciones vienen pre-cargados desde FIFA/Tribuna. Acá editás textos, sponsor PNG y jugadores destacados.
        </p>

        <label className="flex items-center justify-between p-3 border rounded-sm mb-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="pr-3">
            <p className="font-bold text-sm">Activar módulo Mundial 2026</p>
            <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Apagado = no aparece en Deportes (solo se ven los artículos).</p>
          </div>
          <input type="checkbox" checked={!!wc.enabled} onChange={(e) => { setWc({ ...wc, enabled: e.target.checked }); saveWc({ enabled: e.target.checked }); }} className="w-5 h-5" data-testid="wc-enabled" style={{ accentColor: "var(--accent)" }} />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Overline (chip arriba del título)</label>
            <input value={wc.hero_overline} onChange={(e) => setWc({ ...wc, hero_overline: e.target.value })} placeholder="ESPECIAL POPU SPORTS" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="wc-overline" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Título grande</label>
            <input value={wc.hero_title} onChange={(e) => setWc({ ...wc, hero_title: e.target.value })} placeholder="MUNDIAL 2026" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="wc-title" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Subtítulo</label>
            <input value={wc.hero_subtitle} onChange={(e) => setWc({ ...wc, hero_subtitle: e.target.value })} placeholder="Fixture completo, grupos, posiciones..." className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="wc-subtitle" />
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
          <p className="font-bold text-sm uppercase tracking-wider mb-2">💰 Sponsor / Auspiciante</p>
          <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Logo en PNG del cliente. Aparece entre los tabs y el contenido.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>URL del logo PNG (transparente recomendado)</label>
              <input value={wc.sponsor_logo_url} onChange={(e) => setWc({ ...wc, sponsor_logo_url: e.target.value })} placeholder="https://.../logo.png" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="wc-sponsor-logo" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Etiqueta</label>
              <input value={wc.sponsor_label} onChange={(e) => setWc({ ...wc, sponsor_label: e.target.value })} placeholder="AUSPICIA" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="wc-sponsor-label" />
            </div>
            <div className="md:col-span-3">
              <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Link al hacer clic</label>
              <input value={wc.sponsor_link} onChange={(e) => setWc({ ...wc, sponsor_link: e.target.value })} placeholder="https://cliente.com" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="wc-sponsor-link" />
            </div>
            {wc.sponsor_logo_url && (
              <div className="md:col-span-3 mt-2">
                <p className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>Preview:</p>
                <div className="p-3 rounded-sm" style={{ background: "#0a1535" }}>
                  <img src={wc.sponsor_logo_url} alt="sponsor" style={{ maxHeight: 48, maxWidth: 200 }} onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
          <p className="font-bold text-sm uppercase tracking-wider mb-2">⭐ Jugadores destacados</p>
          <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>Aparecen en la tab "Jugadores". Foto recomendada: cuadrada con fondo recortado.</p>

          {(wc.featured_players || []).length > 0 && (
            <ul className="space-y-2 mb-4">
              {wc.featured_players.map((p, i) => (
                <li key={i} className="flex items-center gap-3 p-2 border rounded-sm" style={{ borderColor: "var(--border)" }}>
                  {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-10 h-10 rounded-sm object-cover" /> : <div className="w-10 h-10 bg-gray-700 rounded-sm" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm truncate">{p.name} {p.dorsal && <span className="text-xs opacity-60">#{p.dorsal}</span>}</p>
                    <p className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{p.country} · {p.position}</p>
                  </div>
                  <button onClick={() => removeWcPlayer(i)} className="p-2 hover:text-red-600"><X size={14} /></button>
                </li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <input placeholder="Nombre *" value={wcPlayerDraft.name} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, name: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm md:col-span-2" style={{ borderColor: "var(--border)" }} data-testid="wc-player-name" />
            <input placeholder="Selección (ej: Argentina)" value={wcPlayerDraft.team} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, team: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm md:col-span-2" style={{ borderColor: "var(--border)" }} data-testid="wc-player-team" />
            <input placeholder="País (etiqueta opcional)" value={wcPlayerDraft.country} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, country: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="Flag (ar, br, mx...)" value={wcPlayerDraft.country_flag} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, country_flag: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="Dorsal" value={wcPlayerDraft.dorsal} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, dorsal: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="Edad" value={wcPlayerDraft.age} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, age: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="Posición" value={wcPlayerDraft.position} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, position: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="Altura (cm)" value={wcPlayerDraft.height} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, height: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="Peso (kg)" value={wcPlayerDraft.weight} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, weight: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
            <input placeholder="URL foto" value={wcPlayerDraft.photo_url} onChange={(e) => setWcPlayerDraft({ ...wcPlayerDraft, photo_url: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm md:col-span-4" style={{ borderColor: "var(--border)" }} />
          </div>
          <button onClick={addWcPlayer} className="btn-accent" data-testid="wc-add-player"><Plus size={12} /> Agregar jugador</button>
        </div>

        <button onClick={() => saveWc()} className="btn-accent mt-4" data-testid="wc-save"><Save size={14} /> Guardar Mundial</button>
      </section>

      <section data-testid="ai-config-section">
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2">
          ✨ Inteligencia Artificial
        </h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Controlá uso de IA, modelo y costos. Por defecto usa la Emergent LLM Key (consume tu balance). Podés conectar un proveedor externo compatible con OpenAI (DeepSeek, OpenRouter, Together, etc).
        </p>

        {/* Toggles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <label className="flex items-center justify-between p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="pr-3">
              <p className="font-bold text-sm">Resumen IA (admin)</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>Botón ✨ Resumir IA en capturas. Apagado = modo manual.</p>
            </div>
            <input type="checkbox" checked={!!aiConfig.summary_enabled} onChange={(e) => saveAiConfig({ summary_enabled: e.target.checked })} className="w-5 h-5" data-testid="ai-toggle-summary" style={{ accentColor: "var(--accent)" }} />
          </label>
          <label className="flex items-center justify-between p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <div className="pr-3">
              <p className="font-bold text-sm">POPU IA (chat público)</p>
              <p className="text-xs leading-tight" style={{ color: "var(--text-muted)" }}>El chat del home y header. Apagado = los usuarios no pueden preguntar.</p>
            </div>
            <input type="checkbox" checked={!!aiConfig.popu_ia_enabled} onChange={(e) => saveAiConfig({ popu_ia_enabled: e.target.checked })} className="w-5 h-5" data-testid="ai-toggle-popu" style={{ accentColor: "var(--accent)" }} />
          </label>
        </div>

        {/* Provider config */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Proveedor</label>
            <select value={aiConfig.provider} onChange={(e) => setAiConfig({ ...aiConfig, provider: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="ai-provider-select">
              <option value="emergent">Emergent LLM Key (consume tu balance)</option>
              <option value="openai_compatible">API externa (DeepSeek / OpenRouter / Together / etc)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Modelo</label>
            <input value={aiConfig.model || ""} onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })} placeholder="gpt-4o-mini · claude-sonnet-4-5 · deepseek-chat" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="ai-model-input" />
          </div>
        </div>

        {aiConfig.provider === "openai_compatible" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 p-3 rounded-sm border" style={{ borderColor: "var(--accent)", background: "var(--surface-2)" }}>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>Base URL (compatible OpenAI)</label>
              <input value={aiConfig.custom_base_url || ""} onChange={(e) => setAiConfig({ ...aiConfig, custom_base_url: e.target.value })} placeholder="https://api.deepseek.com/v1" className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm font-mono" style={{ borderColor: "var(--border)" }} data-testid="ai-custom-url" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider mb-1 block" style={{ color: "var(--text-muted)" }}>
                API Key {aiConfig.custom_api_key_masked && <span className="ml-1 opacity-60">(actual: {aiConfig.custom_api_key_masked})</span>}
              </label>
              <input type="password" value={aiConfig.custom_api_key || ""} onChange={(e) => setAiConfig({ ...aiConfig, custom_api_key: e.target.value })} placeholder={aiConfig.custom_api_key_masked ? "Dejar vacío para mantener" : "sk-..."} className="w-full px-3 py-2 border rounded-sm bg-transparent text-sm font-mono" style={{ borderColor: "var(--border)" }} data-testid="ai-custom-key" />
            </div>
          </div>
        )}

        <button onClick={() => saveAiConfig()} className="btn-accent mb-6" data-testid="ai-config-save">
          <Save size={14} /> Guardar config IA
        </button>

        {/* Usage stats */}
        <div className="rounded-sm border p-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="ai-usage-stats">
          <div className="flex items-center justify-between mb-3">
            <p className="font-bold text-sm uppercase tracking-wider">Uso de IA por mes</p>
            <button onClick={resetAiUsage} className="text-xs underline opacity-70 hover:opacity-100" data-testid="ai-usage-reset">Resetear historial</button>
          </div>
          {aiUsage.rows.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>Aún no hay uso registrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    <th className="py-2">Mes</th>
                    <th>Endpoint</th>
                    <th>Modelo</th>
                    <th className="text-right">Calls</th>
                    <th className="text-right">Tokens in</th>
                    <th className="text-right">Tokens out</th>
                    <th className="text-right">Costo ≈ USD</th>
                  </tr>
                </thead>
                <tbody>
                  {aiUsage.rows.map((r, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: "var(--border)" }}>
                      <td className="py-2 font-mono">{r.month}{r.month === aiUsage.current_month && <span className="ml-1 accent-text">●</span>}</td>
                      <td>{r.endpoint}</td>
                      <td className="font-mono">{r.model}</td>
                      <td className="text-right font-bold">{r.calls}</td>
                      <td className="text-right">{r.tokens_in.toLocaleString()}</td>
                      <td className="text-right">{r.tokens_out.toLocaleString()}</td>
                      <td className="text-right font-mono">${r.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-bold" style={{ borderColor: "var(--border)" }}>
                    <td colSpan={6} className="pt-2 text-right">Total acumulado:</td>
                    <td className="pt-2 text-right font-mono accent-text">
                      ${aiUsage.rows.reduce((a, r) => a + r.cost_usd, 0).toFixed(4)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="font-headline uppercase text-2xl mb-3">Bottom Nav (mobile)</h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Botonera inferior estilo app. Configurá entre 2 y 5 items. Cada item: label, ícono y URL destino.
        </p>
        <div className="space-y-2 mb-3">
          {navItems.map((it, i) => (
            <div key={it.k || `nav-${i}`} className="flex gap-2 items-center p-2 border rounded-sm" style={{ borderColor: "var(--border)" }}>
              <input
                placeholder="Label"
                className="w-28 px-2 py-1.5 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                value={it.label}
                onChange={(e) => { const n = [...navItems]; n[i] = { ...n[i], label: e.target.value }; setNavItems(n); }}
                data-testid={`nav-label-${i}`}
              />
              <select
                className="px-2 py-1.5 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                value={it.icon}
                onChange={(e) => { const n = [...navItems]; n[i] = { ...n[i], icon: e.target.value }; setNavItems(n); }}
                data-testid={`nav-icon-${i}`}
              >
                {["home","grid","play","search","bell","news","mail","star","user","video","tv","bookmark","sparkles"].map(ic => (
                  <option key={ic} value={ic}>{ic}</option>
                ))}
              </select>
              <input
                placeholder="/ruta o https://..."
                className="flex-1 px-2 py-1.5 border rounded-sm bg-transparent text-sm"
                style={{ borderColor: "var(--border)" }}
                value={it.to}
                onChange={(e) => { const n = [...navItems]; n[i] = { ...n[i], to: e.target.value }; setNavItems(n); }}
                data-testid={`nav-to-${i}`}
              />
              <button
                type="button"
                onClick={() => setNavItems(navItems.filter((_, idx) => idx !== i))}
                className="p-1.5 hover:text-red-600"
                disabled={navItems.length <= 2}
              ><X size={14} /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setNavItems([...navItems, { k: `i${Date.now()}`, label: "Nuevo", icon: "star", to: "/" }])}
            disabled={navItems.length >= 5}
            className="btn-ghost"
          ><Plus size={12} /> Agregar item</button>
          <button
            type="button"
            onClick={async () => {
              try {
                await api.put("/admin/mobile-nav", { items: navItems });
                toast.success("Bottom nav actualizado");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Error guardando");
              }
            }}
            className="btn-accent ml-auto"
            data-testid="nav-save"
          ><Save size={14} /> Guardar nav</button>
        </div>
      </section>

      <section>
        <h2 className="font-headline uppercase text-2xl mb-3">Notificaciones Push</h2>
        <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
          Funciona en web, Android Chrome y iOS Safari 16.4+. Las breaking news se envían automáticamente.
        </p>
        <form onSubmit={broadcastPush} className="space-y-3 p-4 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div>
            <label className="overline block mb-1">Título *</label>
            <input required maxLength={80} placeholder="Ej: 🔥 Duki sale a Bolivia" value={push.title} onChange={(e) => setPush({ ...push, title: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="push-title" />
          </div>
          <div>
            <label className="overline block mb-1">Mensaje *</label>
            <textarea required maxLength={200} placeholder="Texto corto que aparece en la notificación..." value={push.body} onChange={(e) => setPush({ ...push, body: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent min-h-[70px]" style={{ borderColor: "var(--border)" }} data-testid="push-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">URL al clickear</label>
              <input placeholder="/nota/slug-nota o https://..." value={push.url} onChange={(e) => setPush({ ...push, url: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="push-url" />
            </div>
            <div>
              <label className="overline block mb-1">Texto del botón</label>
              <input placeholder="Leer nota" value={push.action_label} onChange={(e) => setPush({ ...push, action_label: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="push-action-label" />
            </div>
          </div>
          <div>
            <label className="overline block mb-1">URL de imagen banner (opcional)</label>
            <input placeholder="https://... (recomendado 1024x512)" value={push.image} onChange={(e) => setPush({ ...push, image: e.target.value })} className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} data-testid="push-image" />
            {push.image && <img src={push.image} alt="" className="mt-2 w-full max-w-md rounded-sm" onError={(e) => { e.target.style.display = "none"; }} />}
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={push.require_interaction} onChange={(e) => setPush({ ...push, require_interaction: e.target.checked })} />
              Persistente (no se cierra sola)
            </label>
          </div>
          <button type="submit" disabled={pushing} className="btn-accent disabled:opacity-50" data-testid="push-broadcast">
            {pushing ? "Enviando..." : "Enviar notificación a todos"}
          </button>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            💡 Tip: las notas marcadas con <strong>⚡ ÚLTIMO MOMENTO</strong> envían push automáticamente.
          </p>
        </form>
      </section>

      <section>
        <h2 className="font-headline uppercase text-2xl mb-3 flex items-center gap-2"><BarChart3 size={22} /> Encuestas</h2>
        <form onSubmit={createPoll} className="p-4 border rounded-sm space-y-3 mb-4" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <input placeholder="Pregunta" className="w-full px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={newPoll.question} onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })} data-testid="poll-form-question" />
          {newPoll.options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <input placeholder={`Opción ${i + 1}`} className="flex-1 px-3 py-2 border rounded-sm bg-transparent" style={{ borderColor: "var(--border)" }} value={o} onChange={(e) => { const opts = [...newPoll.options]; opts[i] = e.target.value; setNewPoll({ ...newPoll, options: opts }); }} data-testid={`poll-form-option-${i}`} />
              {newPoll.options.length > 2 && <button type="button" onClick={() => setNewPoll({ ...newPoll, options: newPoll.options.filter((_, idx) => idx !== i) })} className="p-2 hover:text-red-600"><X size={14} /></button>}
            </div>
          ))}
          <div className="flex gap-2">
            <button type="button" onClick={() => setNewPoll({ ...newPoll, options: [...newPoll.options, ""] })} className="btn-ghost text-xs"><Plus size={12} /> Opción</button>
            <button type="submit" className="btn-accent ml-auto" data-testid="poll-form-submit">Crear encuesta</button>
          </div>
        </form>
        {polls.length > 0 && (
          <ul className="space-y-2">
            {polls.map((p) => {
              const total = (p.options || []).reduce((acc, o) => acc + (o.votes || 0), 0);
              return (
                <li key={p.id} className="p-3 border rounded-sm flex items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm leading-tight">{p.question}</p>
                    <p className="text-xs" style={{ color: "var(--text-muted)" }}>{p.options?.length} opciones · {total} votos</p>
                  </div>
                  <button onClick={() => deletePoll(p.id)} className="p-2 hover:text-red-600"><Trash2 size={14} /></button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}


const KNOWN_AIRLINES = [
  "BOLIVIANA DE AVIACION",
  "ECO JET",
  "COPA AIRLINES",
  "TAM EP",
  "AMASZONAS",
  "LATAM AIRLINES",
  "AVIANCA",
  "AEROLINEAS ARGENTINAS",
  "GOL",
  "AMERICAN AIRLINES",
];

function AirlineLogosEditor() {
  const [logos, setLogos] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      setLogos(r.data?.airline_logos || {});
      setLoading(false);
    });
  }, []);

  const setUrl = (name, url) => {
    setLogos((prev) => ({ ...prev, [name]: url }));
  };

  const save = async () => {
    try {
      await api.put("/admin/airline-logos", { logos });
      toast.success("Logos guardados");
    } catch {
      toast.error("Error guardando logos");
    }
  };

  if (loading) return <p className="text-sm" style={{ color: "var(--text-muted)" }}>Cargando...</p>;

  return (
    <div className="space-y-2" data-testid="airline-logos-editor">
      {KNOWN_AIRLINES.map((name) => (
        <div key={name} className="flex items-center gap-3 p-2 border rounded-sm" style={{ borderColor: "var(--border)" }}>
          <div className="w-12 h-12 border rounded-sm flex items-center justify-center flex-shrink-0" style={{ borderColor: "var(--accent)", background: "var(--accent)" }}>
            {logos[name] ? (
              <img src={logos[name]} alt={name} className="max-w-full max-h-full object-contain p-1" onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <span className="text-[9px] font-bold text-white opacity-70">SIN LOGO</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider mb-1">{name}</p>
            <input
              value={logos[name] || ""}
              onChange={(e) => setUrl(name, e.target.value)}
              placeholder="https://.../logo.png"
              className="w-full px-2 py-1 border rounded-sm bg-transparent text-xs font-mono"
              style={{ borderColor: "var(--border)" }}
              data-testid={`logo-input-${name.replace(/\s/g, "-")}`}
            />
          </div>
        </div>
      ))}
      <button onClick={save} className="btn-accent mt-3" data-testid="save-airline-logos">
        <Save size={14} /> Guardar logos
      </button>
    </div>
  );
}


const PLATFORMS_LIST = ["instagram", "tiktok", "twitter", "facebook", "youtube"];
const EMPTY_POST = { platform: "instagram", handle: "", verified: true, time: "", text: "", image: "", url: "", likes: "", comments: "", shares: "" };

function SocialPostsEditor() {
  const [posts, setPosts] = useState([]);
  const [draft, setDraft] = useState({ ...EMPTY_POST });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/social/posts").then((r) => {
      setPosts(r.data?.items || []);
      setLoading(false);
    });
  }, []);

  const save = async (next) => {
    try {
      await api.put("/admin/social/posts", { posts: next });
      setPosts(next);
      toast.success("Posts guardados");
    } catch {
      toast.error("Error guardando posts");
    }
  };

  const addDraft = () => {
    if (!draft.handle || !draft.text) { toast.error("Handle y texto son requeridos"); return; }
    const next = [...posts, { ...draft }];
    save(next);
    setDraft({ ...EMPTY_POST });
  };

  const removeAt = (idx) => {
    const next = posts.filter((_, i) => i !== idx);
    save(next);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...posts];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    save(next);
  };

  if (loading) return <p className="text-sm" style={{ color: "var(--text-muted)" }}>Cargando...</p>;

  return (
    <div data-testid="social-posts-editor">
      {posts.length > 0 && (
        <ul className="space-y-2 mb-4">
          {posts.map((p, i) => (
            <li key={i} className="flex items-start gap-3 p-2 border rounded-sm" style={{ borderColor: "var(--border)" }}>
              {p.image ? <img src={p.image} alt="" className="w-14 h-14 rounded-sm object-cover flex-shrink-0" /> : <div className="w-14 h-14 bg-gray-700 rounded-sm flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">{p.handle} <span className="text-xs opacity-60 uppercase">· {p.platform}{p.time ? ` · ${p.time}` : ""}</span></p>
                <p className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{p.text}</p>
                <p className="text-[10px] opacity-50">{p.likes && `❤ ${p.likes}`} {p.comments && `💬 ${p.comments}`} {p.shares && `↗ ${p.shares}`}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => moveUp(i)} disabled={i === 0} className="p-1.5 text-xs disabled:opacity-30" title="Subir">↑</button>
                <button onClick={() => removeAt(i)} className="p-1.5 hover:text-red-600" title="Eliminar"><X size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="p-3 border rounded-sm" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
        <p className="font-bold text-xs uppercase tracking-wider mb-2">Agregar nuevo post</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <select value={draft.platform} onChange={(e) => setDraft({ ...draft, platform: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="social-platform">
            {PLATFORMS_LIST.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
          </select>
          <input placeholder="Handle (@usuario) *" value={draft.handle} onChange={(e) => setDraft({ ...draft, handle: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} data-testid="social-handle" />
          <input placeholder="Tiempo (ej: 2 h)" value={draft.time} onChange={(e) => setDraft({ ...draft, time: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
        </div>
        <textarea placeholder="Texto del post *" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} className="w-full px-2 py-1.5 border rounded-sm bg-transparent text-sm mb-2" style={{ borderColor: "var(--border)" }} rows={2} data-testid="social-text" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input placeholder="URL imagen" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          <input placeholder="URL al post original" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <input placeholder="Likes (ej: 24.7K)" value={draft.likes} onChange={(e) => setDraft({ ...draft, likes: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          <input placeholder="Comentarios" value={draft.comments} onChange={(e) => setDraft({ ...draft, comments: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
          <input placeholder="Compartidos" value={draft.shares} onChange={(e) => setDraft({ ...draft, shares: e.target.value })} className="px-2 py-1.5 border rounded-sm bg-transparent text-sm" style={{ borderColor: "var(--border)" }} />
        </div>
        <label className="flex items-center gap-2 mb-3 text-sm">
          <input type="checkbox" checked={draft.verified} onChange={(e) => setDraft({ ...draft, verified: e.target.checked })} className="w-4 h-4" style={{ accentColor: "var(--accent)" }} />
          Cuenta verificada (badge azul)
        </label>
        <button onClick={addDraft} className="btn-accent" data-testid="add-social-post"><Plus size={12} /> Agregar publicación</button>
      </div>
    </div>
  );
}
